import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";

interface AddressAutocompleteProps {
  inputName: string;
  setError: Dispatch<SetStateAction<{}>>;
}

export function AddressAutocomplete({
  inputName,
  setError,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const { registerDataZustand, setRegisterDataZustand } = useCombinedStore(
    (state) => {
      return {
        registerDataZustand: state.registerDataZustand,
        setRegisterDataZustand: state.setRegisterDataZustand,
      };
    }
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value);

    const updatedRegisterDataZustand = {
      ...registerDataZustand,
      [inputName]: null,
    };

    setRegisterDataZustand(updatedRegisterDataZustand);

    setError((prevState) => {
      return {
        ...prevState,
        [inputName]: null,
      };
    });
  }

  useEffect(() => {
    if (inputRef.current) {
      const autoComplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: ["br"] },
          // `geometry` é a palavra que faltava. Sem ela `place.geometry` volta
          // `undefined` e a coordenada da unidade tem de ser derivada depois, do
          // place_id, por S2 — que dá o ponto do LOGRADOURO e precisa de
          // conferência humana no mapa. Com ela, unidade nova já nasce com
          // lat/lng exatos do lugar escolhido.
          fields: ["place_id", "formatted_address", "geometry"],
        }
      );

      autoComplete.addListener("place_changed", () => {
        const selectedPlace = autoComplete.getPlace();

        if (selectedPlace.formatted_address) {
          const local = selectedPlace.geometry?.location;
          const updatedRegisterDataZustand = {
            ...registerDataZustand,
            [inputName]: selectedPlace.place_id,
            // Guardadas ao lado do endereço para a tela de Unidades já abrir com
            // o pino certo. A gravação em acd."ActSubsidiaryInfo" tipo 21 é do
            // backend — aqui é só o que o autocomplete devolveu.
            [`${inputName}Lat`]: local ? local.lat() : undefined,
            [`${inputName}Lng`]: local ? local.lng() : undefined,
          };

          setRegisterDataZustand(updatedRegisterDataZustand);

          setInputValue(selectedPlace.formatted_address);
        }
      });
    }
  }, [inputName, registerDataZustand, setRegisterDataZustand]);

  useEffect(() => {
    if (registerDataZustand.subsidiaryAddress !== "") {
      let map = new google.maps.Map(document.createElement("div"));
      const service = new google.maps.places.PlacesService(map);

      service.getDetails(
        {
          placeId: registerDataZustand.subsidiaryAddress || "",
          fields: ["formatted_address"],
        },
        (place, status) => {
          if (status === "OK" && place?.formatted_address) {
            setInputValue(place?.formatted_address);
          }
        }
      );
    }
  }, []);

  return (
    <input
      type="text"
      name={inputName}
      ref={inputRef}
      value={inputValue}
      onChange={handleChange}
    />
  );
}
